import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown, ArrowUp, GripVertical, Plus, Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  MAX_SLIDES, MIN_SLIDES, QUOTE_MAX_CHARS, type QuoteSlide,
} from '@/lib/quoteCarousel/types';

interface SlideListProps {
  slides: QuoteSlide[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onChange: (slides: QuoteSlide[]) => void;
  /** Opens the transcript span picker; the page owns the dialog. */
  onAddFromTranscript: () => void;
}

function moveSlide(slides: QuoteSlide[], from: number, to: number): QuoteSlide[] {
  if (from === to || to < 0 || to >= slides.length) return slides;
  const next = slides.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/** Editable, reorderable list of carousel slides: one textarea per slide,
 *  up/down buttons plus native HTML5 drag to reorder, delete (never below
 *  MIN_SLIDES) and add-from-transcript (never above MAX_SLIDES). */
export const SlideList: React.FC<SlideListProps> = ({
  slides, selectedId, onSelect, onChange, onAddFromTranscript,
}) => {
  const { t } = useTranslation();
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  const canDelete = slides.length > MIN_SLIDES;
  const canAdd = slides.length < MAX_SLIDES;

  const updateText = (index: number, text: string) => {
    onChange(slides.map((slide, i) => (i === index ? { ...slide, text } : slide)));
  };

  const removeSlide = (index: number) => {
    if (!canDelete) return;
    onChange(slides.filter((_, i) => i !== index));
  };

  const endDrag = () => {
    setDragIndex(null);
    setDropIndex(null);
  };

  return (
    <div className="flex flex-col gap-3" data-testid="quote-slide-list">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          data-testid={`quote-slide-item-${index}`}
          onDragOver={(event) => {
            if (dragIndex === null) return;
            event.preventDefault();
            setDropIndex(index);
          }}
          onDrop={(event) => {
            event.preventDefault();
            if (dragIndex !== null) onChange(moveSlide(slides, dragIndex, index));
            endDrag();
          }}
          onClick={() => onSelect(slide.id)}
          className={cn(
            'rounded-lg border bg-card p-3 transition-colors',
            selectedId === slide.id ? 'border-primary' : 'border-border',
            dropIndex === index && dragIndex !== index && 'ring-2 ring-primary/50',
            dragIndex === index && 'opacity-50',
          )}
        >
          <div className="mb-2 flex items-center gap-1">
            <span
              draggable
              onDragStart={(event) => {
                event.dataTransfer.effectAllowed = 'move';
                // Firefox needs data set for the drag to start.
                event.dataTransfer.setData('text/plain', slide.id);
                setDragIndex(index);
              }}
              onDragEnd={endDrag}
              className="cursor-grab text-muted-foreground active:cursor-grabbing"
              aria-label={t('quoteCarousel.slides.dragHandle')}
              title={t('quoteCarousel.slides.dragHandle')}
            >
              <GripVertical className="h-4 w-4" />
            </span>
            <span className="text-xs font-medium text-muted-foreground">
              {t('quoteCarousel.slides.slideLabel', { number: index + 1 })}
            </span>
            <div className="ml-auto flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                data-testid={`quote-slide-up-${index}`}
                aria-label={t('quoteCarousel.slides.moveUp')}
                disabled={index === 0}
                onClick={(event) => {
                  event.stopPropagation();
                  onChange(moveSlide(slides, index, index - 1));
                }}
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                data-testid={`quote-slide-down-${index}`}
                aria-label={t('quoteCarousel.slides.moveDown')}
                disabled={index === slides.length - 1}
                onClick={(event) => {
                  event.stopPropagation();
                  onChange(moveSlide(slides, index, index + 1));
                }}
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive"
                data-testid={`quote-slide-delete-${index}`}
                aria-label={t('quoteCarousel.slides.delete')}
                title={canDelete ? undefined : t('quoteCarousel.slides.minSlides', { min: MIN_SLIDES })}
                disabled={!canDelete}
                onClick={(event) => {
                  event.stopPropagation();
                  removeSlide(index);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <Textarea
            data-testid={`quote-slide-text-${index}`}
            value={slide.text}
            maxLength={QUOTE_MAX_CHARS}
            rows={3}
            aria-label={t('quoteCarousel.slides.slideLabel', { number: index + 1 })}
            onFocus={() => onSelect(slide.id)}
            onChange={(event) => updateText(index, event.target.value)}
            className="resize-none text-sm"
          />
          <p className="mt-1 text-right text-xs text-muted-foreground">
            {t('quoteCarousel.slides.charCount', { used: slide.text.length, max: QUOTE_MAX_CHARS })}
          </p>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        data-testid="quote-slide-add"
        disabled={!canAdd}
        title={canAdd ? undefined : t('quoteCarousel.slides.maxSlides', { max: MAX_SLIDES })}
        onClick={onAddFromTranscript}
      >
        <Plus className="mr-1 h-4 w-4" />
        {t('quoteCarousel.slides.addFromTranscript')}
      </Button>
    </div>
  );
};

export default SlideList;
